import React, { useState, useEffect } from 'react';
import './AboutPage.css';
import Logo from './Logo';
import Footer from './Footer';

const AboutPage = ({ onBack, onNavigateToLogin, onNavigateToSignup }) => {
  const [showMobileNavMenu, setShowMobileNavMenu] = useState(false);
  const [showMobileProfileMenu, setShowMobileProfileMenu] = useState(false);

  // Close mobile menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      try {
        if (!e || !e.target) return;
        
        if (showMobileProfileMenu && !e.target.closest('.mobile-profile-icon') && !e.target.closest('.mobile-profile-menu')) {
          setShowMobileProfileMenu(false);
        }
        if (showMobileNavMenu && !e.target.closest('.mobile-hamburger') && !e.target.closest('.mobile-nav-dropdown')) {
          setShowMobileNavMenu(false);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error in click outside handler:', error);
        }
      }
    };

    try {
      document.addEventListener('click', handleClickOutside);
      return () => {
        try {
          document.removeEventListener('click', handleClickOutside);
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error setting up click outside listener:', error);
      }
      return () => {};
    }
  }, [showMobileProfileMenu, showMobileNavMenu]);
  const showNotification = (message) => {
    try {
      if (typeof document === 'undefined' || !document.body) return;
      if (!message) return;

      // Create notification element directly in DOM
      const notification = document.createElement('div');
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        top: 100px;
        left: 50%;
        transform: translateX(-50%) translateY(-20px);
        background-color: #2d3649;
        color: #ffffff;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 0.9rem;
        font-family: 'DM Sans', sans-serif;
        font-weight: 600;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
        z-index: 9999999;
        pointer-events: none;
        white-space: nowrap;
        opacity: 0;
        transition: all 0.3s ease;
      `;

      if (document.body) {
        document.body.appendChild(notification);

        // Trigger fade-in animation
        requestAnimationFrame(() => {
          try {
            if (notification && notification.style) {
              notification.style.opacity = '1';
              notification.style.transform = 'translateX(-50%) translateY(0)';
            }
          } catch (error) {
            // Silently handle animation errors
          }
        });

        // Fade out and remove after 2 seconds
        setTimeout(() => {
          try {
            if (notification && notification.style) {
              notification.style.opacity = '0';
              notification.style.transform = 'translateX(-50%) translateY(-20px)';
              setTimeout(() => {
                try {
                  if (notification && notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                  }
                } catch (error) {
                  // Silently handle cleanup errors
                }
              }, 300);
            }
          } catch (error) {
            // Silently handle errors
          }
        }, 2000);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in showNotification:', error);
      }
    }
  };

  const copyEmailToClipboard = async (email) => {
    try {
      if (!email) return;

      // Try modern clipboard API first
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(email);
          showNotification('Email copied!');
          return;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Clipboard API failed, trying fallback:', err);
          }
        }
      }

      // Fallback for older browsers or when clipboard API fails
      if (typeof document === 'undefined' || !document.body) {
        showNotification('Failed to copy');
        return;
      }

      try {
        const textArea = document.createElement('textarea');
        textArea.value = email;
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        textArea.setAttribute('readonly', '');
        textArea.setAttribute('aria-hidden', 'true');

        if (document.body) {
          document.body.appendChild(textArea);

          // For iOS
          if (navigator && navigator.userAgent && navigator.userAgent.match(/ipad|iphone/i)) {
            try {
              const range = document.createRange();
              range.selectNodeContents(textArea);
              const selection = window.getSelection();
              if (selection) {
                selection.removeAllRanges();
                selection.addRange(range);
              }
              textArea.setSelectionRange(0, 999999);
            } catch (iosErr) {
              // Silently handle iOS selection errors
            }
          } else {
            try {
              textArea.select();
              textArea.setSelectionRange(0, 999999);
            } catch (selectErr) {
              // Silently handle selection errors
            }
          }

          let successful = false;
          try {
            if (document.execCommand) {
              successful = document.execCommand('copy');
            }
          } catch (execErr) {
            // Silently handle execCommand errors
          }

          try {
            if (textArea.parentNode) {
              textArea.parentNode.removeChild(textArea);
            }
          } catch (removeErr) {
            // Silently handle removal errors
          }

          if (successful) {
            showNotification('Email copied!');
          } else {
            showNotification('Failed to copy');
          }
        }
      } catch (fallbackErr) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to copy email:', fallbackErr);
        }
        showNotification('Failed to copy');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error in copyEmailToClipboard:', error);
      }
      showNotification('Failed to copy');
    }
  };

  return (
    <div className="App">
      {/* Navigation - same as home page */}
      <nav className="navigation">
        <div className="container">
          <div className="nav-content">
            {/* Mobile Hamburger Menu */}
            <div className="mobile-nav-controls">
              <button
                className={`mobile-hamburger ${showMobileNavMenu ? 'active' : ''}`}
                onClick={() => setShowMobileNavMenu(!showMobileNavMenu)}
                aria-label="Navigation menu"
              >
                {/* Hamburger lines */}
                <span></span>
                <span></span>
                <span></span>
                {/* Logo icon - appears when active (mobile only) */}
                <img 
                  className="hamburger-logo-icon"
                  src="/Images/holologo_mobile.svg"
                  alt="DermaMatch Logo"
                />
              </button>
            </div>

            {/* Logo placement: top left as per JSON */}
            <div className="nav-logo">
              <button 
                className="logo-button" 
                onClick={onBack}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                <Logo size="medium" />
              </button>
            </div>
            
            {/* Navigation items - centered upper third as per JSON */}
            <div className="nav-items">
              <button className="nav-link" onClick={onBack}>DISCOVER</button>
              <button className="nav-link active" onClick={() => {}}>ABOUT</button>
            </div>
            
            {/* User actions - right aligned as per JSON */}
            <div className="nav-actions">
              <button className="btn btn-secondary nav-btn" onClick={onNavigateToLogin}>LOG IN</button>
              <button className="btn btn-primary nav-btn" onClick={onNavigateToSignup}>SIGN UP</button>
            </div>

            {/* Mobile Profile Icon */}
            <button
              className="mobile-profile-icon"
              onClick={() => setShowMobileProfileMenu(!showMobileProfileMenu)}
              aria-label="Profile menu"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Mobile Nav Dropdown */}
          {showMobileNavMenu && (
            <div className="mobile-nav-dropdown">
              <button
                onClick={() => { onBack(); setShowMobileNavMenu(false); }}
              >
                DISCOVER
              </button>
              <button
                className="active"
                onClick={() => { setShowMobileNavMenu(false); }}
              >
                ABOUT
              </button>
            </div>
          )}

          {/* Mobile Profile Menu */}
          {showMobileProfileMenu && (
            <div className="mobile-profile-menu">
              <button onClick={() => { onNavigateToSignup(); setShowMobileProfileMenu(false); }}>
                SIGN UP
              </button>
              <button onClick={() => { onNavigateToLogin(); setShowMobileProfileMenu(false); }}>
                LOG IN
              </button>
            </div>
          )}
        </div>
      </nav>

      <div className="about-content">
        {/* DermaMatch's Mission Section */}
        <section className="mission-section">
          <div className="about-container">
            <h1 className="section-heading">DermaMatch's Mission</h1>
            <p className="mission-text">
              Whether it's painful acne, early signs of aging, or simply feeling uncomfortable in your own skin, 
              DermaMatch is here to end the exhausting search for what products actually work. We are here to 
              match you with skincare that fits your skin profile down to the last detail. No more wasting money 
              on products that disappoint. Our AI removes the trial and error by building a complete skincare 
              routine tailored to your specific concerns, goals, and sensitivities. Feel confident in your skin 
              and start glowing with DermaMatch.
            </p>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="story-section">
          <div className="about-container">
            <h1 className="section-heading">Our Story</h1>
            <div className="story-text">
              <p>
                Arhaan and Jaden, just two lifelong friends chatting it up on FaceTime from their college dorm 
                rooms at Berkeley and the University of Florida. Both were rambling about the eventful experiences 
                they'd had in just their first semester. While Arhaan was complaining about the tragedy of dining 
                hall food, Jaden suddenly interjected: "Hey, by the way, your skin looks so much better! Was it Accutane?"
              </p>
              <p>
                That comment hit deep. It brought back a three-year skin journey filled with trial and error, 
                frustration, and the daily insecurity of facing a mirror that reflected a face full of acne. It was 
                unreal to think that the same face that once looked like the surface of Mars was now healthy and glistening.
              </p>
              <p>
                And no, it wasn't Accutane or any harsh medications. It was the result of countless hours of research, 
                testing hundreds of products, and ultimately discovering the right skincare products for his unique skin profile.
              </p>
              <p>
                That moment flipped a switch. What if no one had to go through years of failed products just to feel okay 
                looking at their own reflection? What if this trial-and-error could be replaced by something cheaper, faster, 
                and personalized?
              </p>
              <p>
                That's when DermaMatch AI was born, and the real skin journey began.
              </p>
            </div>
          </div>
        </section>

        {/* Meet the Team Section */}
        <section className="team-section">
          <div className="about-container">
            <h1 className="section-heading">Meet the Team</h1>
            
            {/* Founders */}
            <div className="team-subsection">
              <h2 className="subsection-heading">FOUNDERS</h2>
              <div className="team-grid founders-grid">
                <div className="team-member">
                  <div className="profile-card">
                    <div className="profile-gradient">
                      <img src="/Images/Arhaan Sticker.png" alt="Arhaan Garg" className="profile-sticker-arhaan" />
                    </div>
                    <div className="profile-content">
                      <h3 className="member-name">Arhaan Garg</h3>
                      <p className="member-title">Co-Founder, Head of Product</p>
                    </div>
                    <div className="member-social">
                      <a href="https://www.linkedin.com/in/arhaangarg/" className="social-icon linkedin" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.447 20.452H16.893V14.883C16.893 13.555 16.866 11.846 15.041 11.846C13.188 11.846 12.905 13.291 12.905 14.785V20.452H9.351V9H12.765V10.561H12.811C13.288 9.661 14.448 8.711 16.181 8.711C19.782 8.711 20.448 11.081 20.448 14.166V20.452H20.447ZM5.337 7.433C4.193 7.433 3.274 6.507 3.274 5.368C3.274 4.23 4.194 3.305 5.337 3.305C6.477 3.305 7.401 4.23 7.401 5.368C7.401 6.507 6.476 7.433 5.337 7.433ZM7.119 20.452H3.555V9H7.119V20.452ZM22.225 0H1.771C0.792 0 0 0.774 0 1.729V22.271C0 23.227 0.792 24 1.771 24H22.222C23.2 24 24 23.227 24 22.271V1.729C24 0.774 23.2 0 22.222 0H22.225Z" fill="currentColor"/>
                        </svg>
                      </a>
                      <button type="button" onClick={() => copyEmailToClipboard('arhaangarg@berkeley.edu')} className="social-icon email" aria-label="Email">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="team-member">
                  <div className="profile-card">
                    <div className="profile-gradient">
                      <img src="/Images/Jaden Sticker.png" alt="Jaden Autar" className="profile-sticker-jaden" />
                    </div>
                    <div className="profile-content">
                      <h3 className="member-name">Jaden Autar</h3>
                      <p className="member-title">Co-Founder, Head of Operations</p>
                    </div>
                    <div className="member-social">
                      <a href="https://www.linkedin.com/in/jaden-autar-b8aa68340/" className="social-icon linkedin" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.447 20.452H16.893V14.883C16.893 13.555 16.866 11.846 15.041 11.846C13.188 11.846 12.905 13.291 12.905 14.785V20.452H9.351V9H12.765V10.561H12.811C13.288 9.661 14.448 8.711 16.181 8.711C19.782 8.711 20.448 11.081 20.448 14.166V20.452H20.447ZM5.337 7.433C4.193 7.433 3.274 6.507 3.274 5.368C3.274 4.23 4.194 3.305 5.337 3.305C6.477 3.305 7.401 4.23 7.401 5.368C7.401 6.507 6.476 7.433 5.337 7.433ZM7.119 20.452H3.555V9H7.119V20.452ZM22.225 0H1.771C0.792 0 0 0.774 0 1.729V22.271C0 23.227 0.792 24 1.771 24H22.222C23.2 24 24 23.227 24 22.271V1.729C24 0.774 23.2 0 22.222 0H22.225Z" fill="currentColor"/>
                        </svg>
                      </a>
                      <button type="button" onClick={() => copyEmailToClipboard('jaden.autar@ufl.edu')} className="social-icon email" aria-label="Email">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Web & AI Developers */}
            <div className="team-subsection">
              <h2 className="subsection-heading">WEB & AI DEVELOPERS</h2>
              <div className="team-grid developers-grid">
                <div className="team-member">
                  <div className="profile-card">
                    <div className="profile-gradient">
                      <img src="/Images/Kai Sticker updated.png" alt="Kai Navratil" className="profile-sticker-kai" />
                    </div>
                    <div className="profile-content">
                      <h3 className="member-name">Kai Navratil</h3>
                      <p className="member-title">Head of Technology</p>
                    </div>
                    <div className="member-social">
                      <a href="https://github.com/KairiNavratil" className="social-icon github" aria-label="GitHub" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 0C5.374 0 0 5.373 0 12 0 17.302 3.438 21.8 8.207 23.387c.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" fill="currentColor"/>
                        </svg>
                      </a>
                      <a href="https://www.linkedin.com/in/kairi-navratil/" className="social-icon linkedin" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.447 20.452H16.893V14.883C16.893 13.555 16.866 11.846 15.041 11.846C13.188 11.846 12.905 13.291 12.905 14.785V20.452H9.351V9H12.765V10.561H12.811C13.288 9.661 14.448 8.711 16.181 8.711C19.782 8.711 20.448 11.081 20.448 14.166V20.452H20.447ZM5.337 7.433C4.193 7.433 3.274 6.507 3.274 5.368C3.274 4.23 4.194 3.305 5.337 3.305C6.477 3.305 7.401 4.23 7.401 5.368C7.401 6.507 6.476 7.433 5.337 7.433ZM7.119 20.452H3.555V9H7.119V20.452ZM22.225 0H1.771C0.792 0 0 0.774 0 1.729V22.271C0 23.227 0.792 24 1.771 24H22.222C23.2 24 24 23.227 24 22.271V1.729C24 0.774 23.2 0 22.222 0H22.225Z" fill="currentColor"/>
                        </svg>
                      </a>
                      <button type="button" onClick={() => copyEmailToClipboard('kaijnav@gmail.com')} className="social-icon email" aria-label="Email">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="team-member">
                  <div className="profile-card">
                    <div className="profile-gradient">
                      <img src="/Images/Ricardo Sticker updated.png" alt="Ricardo Escalante" className="profile-sticker-ricardo" />
                    </div>
                    <div className="profile-content">
                      <h3 className="member-name">Ricardo Escalante</h3>
                      <p className="member-title">Software Engineer</p>
                    </div>
                    <div className="member-social">
                      <a href="https://github.com/ricardo-escalante06" className="social-icon github" aria-label="GitHub" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 0C5.374 0 0 5.373 0 12 0 17.302 3.438 21.8 8.207 23.387c.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" fill="currentColor"/>
                        </svg>
                      </a>
                      <a href="https://www.linkedin.com/in/ricardo-escalante-687a2b31a/" className="social-icon linkedin" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.447 20.452H16.893V14.883C16.893 13.555 16.866 11.846 15.041 11.846C13.188 11.846 12.905 13.291 12.905 14.785V20.452H9.351V9H12.765V10.561H12.811C13.288 9.661 14.448 8.711 16.181 8.711C19.782 8.711 20.448 11.081 20.448 14.166V20.452H20.447ZM5.337 7.433C4.193 7.433 3.274 6.507 3.274 5.368C3.274 4.23 4.194 3.305 5.337 3.305C6.477 3.305 7.401 4.23 7.401 5.368C7.401 6.507 6.476 7.433 5.337 7.433ZM7.119 20.452H3.555V9H7.119V20.452ZM22.225 0H1.771C0.792 0 0 0.774 0 1.729V22.271C0 23.227 0.792 24 1.771 24H22.222C23.2 24 24 23.227 24 22.271V1.729C24 0.774 23.2 0 22.222 0H22.225Z" fill="currentColor"/>
                        </svg>
                      </a>
                      <button type="button" onClick={() => copyEmailToClipboard('escalante.ricardo06@gmail.com')} className="social-icon email" aria-label="Email">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Design and Marketing */}
            <div className="team-subsection">
              <h2 className="subsection-heading">DESIGN AND MARKETING</h2>
              <div className="team-grid designers-grid">
                <div className="team-member">
                  <div className="profile-card">
                    <div className="profile-gradient">
                      <img src="/Images/joanna_portrait.png" alt="Joanna Wong" className="profile-sticker-joanna" />
                    </div>
                    <div className="profile-content">
                      <h3 className="member-name">Joanna Wong</h3>
                      <p className="member-title">Lead UI/UX Designer</p>
                    </div>
                    <div className="member-social">
                      <a href="https://joanna-wongs-portfolio.webflow.io/" className="social-icon link" aria-label="Portfolio" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </a>
                      <a href="https://www.linkedin.com/in/joanna-wong-58b892244/" className="social-icon linkedin" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.447 20.452H16.893V14.883C16.893 13.555 16.866 11.846 15.041 11.846C13.188 11.846 12.905 13.291 12.905 14.785V20.452H9.351V9H12.765V10.561H12.811C13.288 9.661 14.448 8.711 16.181 8.711C19.782 8.711 20.448 11.081 20.448 14.166V20.452H20.447ZM5.337 7.433C4.193 7.433 3.274 6.507 3.274 5.368C3.274 4.23 4.194 3.305 5.337 3.305C6.477 3.305 7.401 4.23 7.401 5.368C7.401 6.507 6.476 7.433 5.337 7.433ZM7.119 20.452H3.555V9H7.119V20.452ZM22.225 0H1.771C0.792 0 0 0.774 0 1.729V22.271C0 23.227 0.792 24 1.771 24H22.222C23.2 24 24 23.227 24 22.271V1.729C24 0.774 23.2 0 22.222 0H22.225Z" fill="currentColor"/>
                        </svg>
                      </a>
                      <button type="button" onClick={() => copyEmailToClipboard('wongjoanna1358@berkeley.edu')} className="social-icon email" aria-label="Email">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="team-member">
                  <div className="profile-card">
                    <div className="profile-gradient">
                      <img src="/Images/Angela Sticker.png" alt="Angela Luo" className="profile-sticker-angela" />
                    </div>
                    <div className="profile-content">
                      <h3 className="member-name">Angela Luo</h3>
                      <p className="member-title">UI/UX Designer</p>
                    </div>
                    <div className="member-social">
                      <a href="#" className="social-icon link" aria-label="Portfolio">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </a>
                      <a href="https://www.linkedin.com/in/angelaxyluo/" className="social-icon linkedin" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.447 20.452H16.893V14.883C16.893 13.555 16.866 11.846 15.041 11.846C13.188 11.846 12.905 13.291 12.905 14.785V20.452H9.351V9H12.765V10.561H12.811C13.288 9.661 14.448 8.711 16.181 8.711C19.782 8.711 20.448 11.081 20.448 14.166V20.452H20.447ZM5.337 7.433C4.193 7.433 3.274 6.507 3.274 5.368C3.274 4.23 4.194 3.305 5.337 3.305C6.477 3.305 7.401 4.23 7.401 5.368C7.401 6.507 6.476 7.433 5.337 7.433ZM7.119 20.452H3.555V9H7.119V20.452ZM22.225 0H1.771C0.792 0 0 0.774 0 1.729V22.271C0 23.227 0.792 24 1.771 24H22.222C23.2 24 24 23.227 24 22.271V1.729C24 0.774 23.2 0 22.222 0H22.225Z" fill="currentColor"/>
                        </svg>
                      </a>
                      <button type="button" onClick={() => copyEmailToClipboard('angela.xy.luo@berkeley.edu')} className="social-icon email" aria-label="Email">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="team-member">
                  <div className="profile-card">
                    <div className="profile-gradient">
                      <img src="/Images/Minju Sticker.png" alt="Minju Kim" className="profile-sticker-minju" />
                    </div>
                    <div className="profile-content">
                      <h3 className="member-name">Minju Kim</h3>
                      <p className="member-title">Head of Growth & GTM</p>
                    </div>
                    <div className="member-social">
                      <a href="https://www.linkedin.com/in/minju-kim-b7bb64212/" className="social-icon linkedin" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.447 20.452H16.893V14.883C16.893 13.555 16.866 11.846 15.041 11.846C13.188 11.846 12.905 13.291 12.905 14.785V20.452H9.351V9H12.765V10.561H12.811C13.288 9.661 14.448 8.711 16.181 8.711C19.782 8.711 20.448 11.081 20.448 14.166V20.452H20.447ZM5.337 7.433C4.193 7.433 3.274 6.507 3.274 5.368C3.274 4.23 4.194 3.305 5.337 3.305C6.477 3.305 7.401 4.23 7.401 5.368C7.401 6.507 6.476 7.433 5.337 7.433ZM7.119 20.452H3.555V9H7.119V20.452ZM22.225 0H1.771C0.792 0 0 0.774 0 1.729V22.271C0 23.227 0.792 24 1.771 24H22.222C23.2 24 24 23.227 24 22.271V1.729C24 0.774 23.2 0 22.222 0H22.225Z" fill="currentColor"/>
                        </svg>
                      </a>
                      <button type="button" onClick={() => copyEmailToClipboard('minjukim@berkeley.edu')} className="social-icon email" aria-label="Email">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Footer */}
      <Footer 
        onNavigateToHome={onBack}
        onNavigateToAbout={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      />
    </div>
  );
};

export default AboutPage;
